import { Controller } from "@hotwired/stimulus"
import { FillBlankNode } from "../extensions/fill_blank_node"
import { createFillBlankShortcutExtension } from "../extensions/fill_blank_input_rule"

export default class extends Controller {
  static targets = ["blanksField", "editorContainer", "popup",
    "popupAnswer", "popupHint", "popupAlternatives",
    "optionsList", "optionsError"]
  static values = {
    mode: { type: String, default: "typing" },
    noCorrectMsg: { type: String, default: "" },
    minOptionsMsg: { type: String, default: "" }
  }

  connect() {
    this.blanks = this.#parseBlanks()

    document.addEventListener("rhino-before-initialize", this.#registerExtension)
    this.element.addEventListener("fill-blank:edit", this.#onBlankEdit)
  }

  disconnect() {
    document.removeEventListener("rhino-before-initialize", this.#registerExtension)
    this.element.removeEventListener("fill-blank:edit", this.#onBlankEdit)
  }

  // --- Toolbar button action ---

  insertBlank() {
    const rhinoEditor = this.element.querySelector("rhino-editor")
    if (!rhinoEditor?.editor) return

    const editor = rhinoEditor.editor
    const blankId = this.#generateBlankId()
    const { from, to, empty } = editor.state.selection

    let answer = ""
    if (!empty) {
      answer = editor.state.doc.textBetween(from, to, " ").trim()
    }

    if (this.#isMultipleChoice()) {
      this.blanks[blankId] = { answer, hint: "", alternatives: [], options: answer ? [answer] : [] }
    } else {
      this.blanks[blankId] = { answer, hint: "", alternatives: [] }
    }
    this.#syncBlanksField()

    const content = { type: "fillBlank", attrs: { "data-blank-id": blankId } }
    if (empty) {
      editor.chain().focus().insertContent(content).run()
    } else {
      editor.chain().focus().insertContentAt({ from, to }, content).run()
    }

    if (!answer || this.#isMultipleChoice()) {
      this.#openPopup(blankId)
    }
  }

  // --- Popup actions ---

  savePopup() {
    const blankId = this.popupTarget.dataset.blankId
    if (!blankId || !this.blanks[blankId]) return

    if (this.#isMultipleChoice()) {
      const rows = this.#collectOptionRows()
      const nonEmpty = rows.filter(r => r.text.length > 0)
      if (nonEmpty.length < 2) {
        this.#showOptionsError(this.minOptionsMsgValue)
        return
      }
      const checked = nonEmpty.filter(r => r.checked)
      if (checked.length === 0) {
        this.#showOptionsError(this.noCorrectMsgValue)
        return
      }
      const answer = checked[0].text
      const alternatives = checked.slice(1).map(r => r.text)
      const options = nonEmpty.map(r => r.text)

      this.blanks[blankId].answer = answer
      this.blanks[blankId].alternatives = alternatives
      this.blanks[blankId].options = options
      this.blanks[blankId].hint = this.hasPopupHintTarget ? this.popupHintTarget.value.trim() : ""
      this.#hideOptionsError()
    } else {
      this.blanks[blankId].answer = this.popupAnswerTarget.value.trim()
      this.blanks[blankId].hint = this.popupHintTarget.value.trim()
      this.blanks[blankId].alternatives = this.popupAlternativesTarget.value
        .split(",")
        .map((a) => a.trim())
        .filter(Boolean)
    }

    this.#syncBlanksField()
    this.#refreshNodeViews()
    this.#closePopup()
  }

  deleteBlank() {
    const blankId = this.popupTarget.dataset.blankId
    if (!blankId) return

    const rhinoEditor = this.element.querySelector("rhino-editor")
    if (!rhinoEditor?.editor) return

    const editor = rhinoEditor.editor
    const { doc } = editor.state
    const answer = this.blanks[blankId]?.answer || ""

    doc.descendants((node, pos) => {
      if (node.type.name === "fillBlank" && node.attrs["data-blank-id"] === blankId) {
        editor
          .chain()
          .focus()
          .command(({ tr }) => {
            tr.replaceWith(pos, pos + node.nodeSize, answer ? editor.state.schema.text(answer) : editor.state.schema.text(""))
            return true
          })
          .run()
        return false
      }
    })

    delete this.blanks[blankId]
    this.#syncBlanksField()
    this.#closePopup()
  }

  closePopup() {
    this.#closePopup()
  }

  // --- Multiple-choice options actions ---

  addOption(event) {
    event?.preventDefault()
    const row = this.#buildOptionRow({ text: "", checked: false })
    this.optionsListTarget.appendChild(row)
    row.querySelector("input[type=text]").focus()
  }

  removeOption(event) {
    event?.preventDefault()
    const row = event.currentTarget.closest("[data-fill-blanks-editor-row]")
    if (row) row.remove()
  }

  // --- Private ---

  #registerExtension = (e) => {
    if (this.element.contains(e.target)) {
      const shortcut = createFillBlankShortcutExtension({
        getMode: () => this.modeValue,
        getBlanks: () => this.blanks,
        generateBlankId: () => this.#generateBlankId(),
        syncBlanks: () => this.#syncBlanksField(),
      })
      e.target.addExtensions(FillBlankNode, shortcut)
    }
  }

  #onBlankEdit = (e) => {
    const { blankId } = e.detail
    if (blankId) this.#openPopup(blankId)
  }

  #openPopup(blankId) {
    if (!this.hasPopupTarget) return

    const data = this.blanks[blankId] || { answer: "", hint: "", alternatives: [], options: [] }
    this.popupTarget.dataset.blankId = blankId

    if (this.#isMultipleChoice()) {
      this.#renderOptionRows(data)
      if (this.hasPopupHintTarget) this.popupHintTarget.value = data.hint || ""
      this.#hideOptionsError()
      this.popupTarget.classList.remove("hidden")
      const firstInput = this.optionsListTarget.querySelector("input[type=text]")
      if (firstInput) firstInput.focus()
    } else {
      this.popupAnswerTarget.value = data.answer || ""
      this.popupHintTarget.value = data.hint || ""
      this.popupAlternativesTarget.value = (data.alternatives || []).join(", ")
      this.popupTarget.classList.remove("hidden")
      this.popupAnswerTarget.focus()
    }
  }

  #closePopup() {
    if (!this.hasPopupTarget) return
    this.popupTarget.classList.add("hidden")
    this.popupTarget.dataset.blankId = ""
    this.#hideOptionsError()
  }

  #parseBlanks() {
    if (!this.hasBlanksFieldTarget) return {}
    try {
      return JSON.parse(this.blanksFieldTarget.value || "{}")
    } catch {
      return {}
    }
  }

  #syncBlanksField() {
    if (!this.hasBlanksFieldTarget) return
    this.blanksFieldTarget.value = JSON.stringify(this.blanks)
  }

  #refreshNodeViews() {
    const rhinoEditor = this.element.querySelector("rhino-editor")
    if (!rhinoEditor?.editor) return
    const { tr } = rhinoEditor.editor.state
    rhinoEditor.editor.view.dispatch(tr)
  }

  #generateBlankId() {
    return Math.random().toString(16).substring(2, 6)
  }

  #isMultipleChoice() {
    return this.modeValue === "multiple_choice"
  }

  #renderOptionRows(data) {
    if (!this.hasOptionsListTarget) return
    this.optionsListTarget.innerHTML = ""

    const options = Array.isArray(data.options) ? data.options : []
    const correctSet = new Set([data.answer, ...(data.alternatives || [])].filter(Boolean))

    const rows = options.length > 0
      ? options.map(text => ({ text, checked: correctSet.has(text) }))
      : [{ text: "", checked: false }, { text: "", checked: false }]

    rows.forEach(r => this.optionsListTarget.appendChild(this.#buildOptionRow(r)))
  }

  #buildOptionRow({ text, checked }) {
    const row = document.createElement("div")
    row.dataset.fillBlanksEditorRow = "true"
    row.className = "flex items-center gap-2"

    const checkbox = document.createElement("input")
    checkbox.type = "checkbox"
    checkbox.checked = !!checked
    checkbox.className = "h-4 w-4 rounded border-surface-border-strong text-brand-500 focus:ring-brand-500"

    const input = document.createElement("input")
    input.type = "text"
    input.value = text || ""
    input.autocomplete = "off"
    input.className = "flex-1 px-3 py-1.5 text-sm border border-surface-border-strong rounded-ui focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"

    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.dataset.action = "click->fill-blanks-editor#removeOption"
    removeBtn.className = "text-content-muted hover:text-error-dark px-1 text-lg leading-none"
    removeBtn.setAttribute("aria-label", "Remove option")
    removeBtn.textContent = "×"

    row.appendChild(checkbox)
    row.appendChild(input)
    row.appendChild(removeBtn)
    return row
  }

  #collectOptionRows() {
    if (!this.hasOptionsListTarget) return []
    return Array.from(this.optionsListTarget.querySelectorAll("[data-fill-blanks-editor-row]")).map(row => {
      const checkbox = row.querySelector("input[type=checkbox]")
      const text = row.querySelector("input[type=text]").value.trim()
      return { text, checked: !!checkbox?.checked }
    })
  }

  #showOptionsError(msg) {
    if (!this.hasOptionsErrorTarget) return
    this.optionsErrorTarget.textContent = msg
    this.optionsErrorTarget.classList.remove("hidden")
  }

  #hideOptionsError() {
    if (!this.hasOptionsErrorTarget) return
    this.optionsErrorTarget.textContent = ""
    this.optionsErrorTarget.classList.add("hidden")
  }
}
